// src/features/invoice/invoiceSlice.js
import {createSlice} from '@reduxjs/toolkit';
import {fetchInvoice, fetchOrderSummary, payInvoice, setFinishingOptions,} from './invoiceActions';

/* ------------------------------------------------------------------ */
/* Initial shape keeps every request distinct to simplify UI logic    */
/* ------------------------------------------------------------------ */
const initialState = {
    invoice: [],           // result of fetchInvoice
    summary: [],           // result of fetchOrderSummary
    paymentStatus: [],     // “Invoice Payment Success” | null
    finishingStatus: [],   // “Finishing options set”    | null
    loading: false,
    error: null,
};

const invoiceSlice = createSlice({
    name: 'invoice',
    initialState,
    reducers: {
        resetInvoiceState: () => initialState,     // optional helper
    },
    extraReducers: builder => {
        /* ---------- Shared pending / rejected helpers ---------- */
        const setPending = state => {
            state.loading = true;
            state.error = null;
        };
        const setRejected = (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Unknown error';
        };

        /* ---------- fetchInvoice ---------- */
        builder
            .addCase(fetchInvoice.pending, setPending)
            .addCase(fetchInvoice.fulfilled, (state, action) => {
                state.loading = false;
                state.invoice = action.payload;
            })
            .addCase(fetchInvoice.rejected, setRejected);

        /* ---------- fetchOrderSummary ---------- */
        builder
            .addCase(fetchOrderSummary.pending, setPending)
            .addCase(fetchOrderSummary.fulfilled, (state, action) => {
                state.loading = false;
                state.summary = action.payload;
            })
            .addCase(fetchOrderSummary.rejected, setRejected);

        /* ---------- payInvoice ---------- */
        builder
            .addCase(payInvoice.pending, setPending)
            .addCase(payInvoice.fulfilled, (state, action) => {
                state.loading = false;
                state.paymentStatus = action.payload;   // success string
            })
            .addCase(payInvoice.rejected, setRejected);

        /* ---------- setFinishingOptions ---------- */
        builder
            .addCase(setFinishingOptions.pending, setPending)
            .addCase(setFinishingOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.finishingStatus = action.payload;
            })
            .addCase(setFinishingOptions.rejected, setRejected);
    },
});

export const {resetInvoiceState} = invoiceSlice.actions;
export default invoiceSlice.reducer;

/* ------------------------------------------------------------------ */
/* Optional Selectors – keeps components clean                         */
/* ------------------------------------------------------------------ */
export const selectInvoice = state => state.invoice.invoice;
export const selectSummary = state => state.invoice.summary;
export const selectInvoiceLoading = state => state.invoice.loading;
export const selectInvoiceError = state => state.invoice.error;
export const selectPaymentStatus = state => state.invoice.paymentStatus;
export const selectFinishingStatus = state => state.invoice.finishingStatus;
